import { cva } from "class-variance-authority";
import { cn } from "./utils.js";

/**
 * Shared status pill surface: soft tint + matching border/text.
 * Hover twins neutralize Badge default hover when variant isn't `status`,
 * and keep raw className consumers (asset overview) from flashing primary.
 */
const STATUS_PILL = {
  normal:
    "border-asset-running/40 bg-asset-running/10 text-green-700 hover:bg-asset-running/10 dark:border-asset-running/50 dark:bg-asset-running/15 dark:text-green-400 dark:hover:bg-asset-running/15",
  danger:
    "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-50 dark:border-orange-500/60 dark:bg-orange-500/15 dark:text-orange-300 dark:hover:bg-orange-500/15",
  alerted:
    "border-asset-warning/40 bg-asset-warning/10 text-amber-800 hover:bg-asset-warning/10 dark:border-asset-warning/50 dark:bg-asset-warning/15 dark:text-amber-300 dark:hover:bg-asset-warning/15",
  nodata:
    "border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-100 dark:border-slate-500/60 dark:bg-slate-500/15 dark:text-slate-300 dark:hover:bg-slate-500/15",
  standby:
    "border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-100 dark:border-slate-400/50 dark:bg-slate-400/15 dark:text-slate-300 dark:hover:bg-slate-400/15",
  offline:
    "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/10 dark:border-destructive/50 dark:bg-destructive/15 dark:text-red-300 dark:hover:bg-destructive/15",
  unknown:
    "border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-100 dark:border-slate-500/60 dark:bg-slate-500/15 dark:text-slate-400 dark:hover:bg-slate-500/15",
};

/**
 * Unified status badge configuration using class-variance-authority
 */
export const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
  {
    variants: {
      status: {
        normal: STATUS_PILL.normal,
        danger: STATUS_PILL.danger,
        alerted: STATUS_PILL.alerted,
        nodata: STATUS_PILL.nodata,
        standby: STATUS_PILL.standby,
        offline: STATUS_PILL.offline,
        unknown: STATUS_PILL.unknown,
      },
    },
  }
);

/**
 * Status dot variants for indicators — solid accent matching pill text/border
 */
export const statusDotVariants = cva(
  "inline-block h-2 w-2 shrink-0 rounded-full",
  {
    variants: {
      status: {
        normal: "bg-asset-running dark:bg-green-400",
        danger: "bg-orange-500 dark:bg-orange-400",
        alerted: "bg-asset-warning dark:bg-amber-400",
        nodata: "bg-slate-500 dark:bg-slate-400",
        standby: "bg-slate-400 dark:bg-slate-400",
        offline: "bg-destructive",
        unknown: "bg-slate-400 dark:bg-slate-500",
      },
    },
  }
);

/**
 * Status color mapping for inline styles / map markers (hex)
 */
export const statusColorsHex = {
  normal: "#1F9D55", // Detechtion.AI success green
  danger: "#f97316", // orange-500
  alerted: "#F5A524", // Detechtion.AI safety amber
  nodata: "#6b7280", // gray-500
  standby: "#9ca3af", // gray-400
  offline: "#E4232A", // Detechtion Red
  unknown: "#c0c0c0",
};

/** Map raw status (first word) to CVA variant key; API values (running/faulted/shutdown) map to normal/danger/offline. */
const STATUS_TO_VARIANT = {
  normal: "normal",
  danger: "danger",
  alerted: "alerted",
  nodata: "nodata",
  standby: "standby",
  offline: "offline",
  unknown: "unknown",
  running: "normal",
  faulted: "danger",
  shutdown: "offline",
};

function normalizeStatus(status) {
  if (status == null || typeof status !== "string") return "unknown";
  const normalized = status.trim().toLowerCase();
  if (normalized.startsWith("no data") || normalized.startsWith("nodata")) {
    return "nodata";
  }
  const first = normalized.split(/\s+/)[0];
  return (first && STATUS_TO_VARIANT[first]) || "unknown";
}

/**
 * Get status badge classes
 * @param {string} status - Status value
 * @param {string} [className] - Additional classes
 * @returns {string} Combined class names
 */
export function getStatusBadgeClass(status, className) {
  return cn(
    statusBadgeVariants({ status: normalizeStatus(status) }),
    className
  );
}

/**
 * Get status dot classes
 * @param {string} status - Status value
 * @param {string} [className] - Additional classes
 * @returns {string} Combined class names
 */
export function getStatusDotClass(status, className) {
  return cn(statusDotVariants({ status: normalizeStatus(status) }), className);
}

/**
 * Get status color for inline styles (hex values)
 * @param {string} status - Status value
 * @returns {string} Hex color value
 */
export function getStatusColorHex(status) {
  return statusColorsHex[normalizeStatus(status)] || statusColorsHex.offline;
}

/**
 * Get status display text
 * @param {string} status - Status value
 * @returns {string} Display text
 */
export function getStatusText(status) {
  const statusMap = {
    normal: "Normal",
    danger: "Danger",
    alerted: "Alerted",
    nodata: "No Data",
    standby: "Standby",
    offline: "Offline",
    unknown: "Unknown",
  };
  return statusMap[status] || status;
}

/**
 * Status priority mapping for sorting and cluster determination
 * Higher numbers = more critical/urgent status
 * Used for determining which status to show when multiple statuses are present
 */
export const STATUS_PRIORITY = {
  offline: 6,
  danger: 5,
  alerted: 4,
  normal: 3,
  standby: 2,
  nodata: 1,
  unknown: 0,
  available: 0,
};

/**
 * Get priority value for a status
 * @param {string} status - Status value
 * @returns {number} Priority value (higher = more critical)
 */
export function getStatusPriority(status) {
  if (!status || typeof status !== "string") return 0;
  return STATUS_PRIORITY[status.toLowerCase()] ?? 0;
}

/**
 * Get status color classes only (without base badge layout classes)
 * @param {string} status - Status display key (PascalCase: Normal, NoData, …)
 * @returns {string} Color classes
 */
export function getStatusColorClasses(status) {
  const keyMap = {
    Normal: "normal",
    Danger: "danger",
    Alerted: "alerted",
    NoData: "nodata",
    Standby: "standby",
    Offline: "offline",
    Unknown: "unknown",
  };
  const key = keyMap[status] || "offline";
  return STATUS_PILL[key];
}
